import React, { useState, useEffect, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts"
import { getImpuestosDiario } from "../api"
import {
  formatCurrency, formatNumber, formatPercent, formatFecha,
  familiaConfig, descargarCSV,
} from "../helpers"

const DiarioTab = ({ filtros }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [diaExpandido, setDiaExpandido] = useState(null)

  const cargar = useCallback(async () => {
    if (!filtros) return
    setLoading(true)
    setError("")
    setData(null)
    try {
      const r = await getImpuestosDiario(filtros)
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      setData(body)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el detalle diario.")
    } finally {
      setLoading(false)
    }
  }, [filtros])

  useEffect(() => { cargar() }, [cargar])

  const tendencia = useMemo(() => {
    return (data?.tendencia_diaria || []).map(d => ({
      fecha: d.fecha,
      base: Number(d.base_gravable) || 0,
      impuesto: Number(d.valor_impuesto) || 0,
      lineas: Number(d.cantidad_lineas) || 0,
    }))
  }, [data])

  const promedios = useMemo(() => {
    if (tendencia.length === 0) return null
    const tot = tendencia.reduce((acc, d) => ({
      base: acc.base + d.base, impuesto: acc.impuesto + d.impuesto, lineas: acc.lineas + d.lineas,
    }), { base: 0, impuesto: 0, lineas: 0 })
    return {
      base_promedio: tot.base / tendencia.length,
      impuesto_promedio: tot.impuesto / tendencia.length,
      lineas_promedio: tot.lineas / tendencia.length,
      total_dias: tendencia.length,
    }
  }, [tendencia])

  const handleExportCSV = () => {
    const headers = ["Fecha", "Líneas", "Base gravable", "Valor impuesto"]
    const rows = tendencia.map(d => [d.fecha, d.lineas, d.base, d.impuesto])
    descargarCSV(`impuestos_diario_${new Date().toISOString().split("T")[0]}.csv`, headers, rows)
  }

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b", background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
        <Spinner color="primary" />
        <div className="mt-2">Cargando detalle diario…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        padding: "12px 16px", background: "#fef2f2", color: "#991b1b",
        border: "1px solid #fecaca", borderRadius: 10,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <i className="mdi mdi-alert-circle" />
        <span style={{ flex: 1 }}>{error}</span>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={cargar}>Reintentar</button>
      </div>
    )
  }

  if (!data || tendencia.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b", background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
        <i className="mdi mdi-calendar-blank-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin datos diarios.</div>
      </div>
    )
  }

  const dias = data.dias || []

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* KPIs promedios diarios */}
      {promedios && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={miniCard}>
            <div style={miniLabel}>Días</div>
            <div style={miniValue}>{formatNumber(promedios.total_dias)}</div>
          </div>
          <div style={miniCard}>
            <div style={miniLabel}>Base promedio/día</div>
            <div style={miniValue}>{formatCurrency(promedios.base_promedio)}</div>
          </div>
          <div style={miniCard}>
            <div style={miniLabel}>Impuesto promedio/día</div>
            <div style={{ ...miniValue, color: "#10b981" }}>{formatCurrency(promedios.impuesto_promedio)}</div>
          </div>
          <div style={miniCard}>
            <div style={miniLabel}>Líneas promedio/día</div>
            <div style={miniValue}>{formatNumber(promedios.lineas_promedio, 0)}</div>
          </div>
        </div>
      )}

      {/* Gráfico tendencia */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
        <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-2">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-chart-line me-2 text-primary" />
            Tendencia diaria · base gravable y valor impuesto
          </h6>
          <button type="button" className="btn btn-sm btn-outline-success" onClick={handleExportCSV}>
            <i className="mdi mdi-file-delimited me-1" />
            CSV
          </button>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={tendencia} margin={{ left: 0, right: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="fecha" tick={{ fontSize: 11 }} tickFormatter={formatFecha} />
            <YAxis yAxisId="left"  tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
            <Tooltip
              labelFormatter={l => formatFecha(l)}
              formatter={(v, n) => [formatCurrency(v), n === "base" ? "Base gravable" : "Valor impuesto"]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left"  dataKey="base"     name="Base gravable" fill="#94a3b8" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="impuesto" name="Impuesto" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Tabla de días con expand */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-calendar-text-outline me-2 text-primary" />
            Detalle por día · {formatNumber(dias.length)} días
          </h6>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            Click en una fila para ver el desglose por sucursal.
          </div>
        </div>
        <div style={{ overflowX: "auto", maxHeight: "60vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Fecha</th>
                <th style={{ ...th, textAlign: "right" }}>Sucursales</th>
                <th style={{ ...th, textAlign: "right" }}>Líneas</th>
                <th style={{ ...th, textAlign: "right" }}>Base gravable</th>
                <th style={{ ...th, textAlign: "right" }}>Impuestos</th>
                <th style={{ ...th, textAlign: "right" }}>Con imp.</th>
                <th style={{ ...th, textAlign: "right" }}>Excluidos</th>
                <th style={{ ...th, textAlign: "center" }}>% Excluido</th>
                <th style={{ ...th, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {dias.map((d, i) => {
                const tot = d.totales_dia || {}
                const totalExcluidoDia = tot.total_excluidos ?? tot.total_exentos ?? 0
                const pctExcluidoDia = tot.porcentaje_excluido ?? tot.porcentaje_exento ?? 0
                const expandido = diaExpandido === d.fecha
                return (
                  <React.Fragment key={d.fecha}>
                    <tr
                      style={{
                        background: expandido ? "#eff6ff" : (i % 2 === 0 ? "#fff" : "#fafbfc"),
                        cursor: "pointer", transition: "background 0.15s",
                      }}
                      onClick={() => setDiaExpandido(expandido ? null : d.fecha)}
                    >
                      <td style={{ ...td, fontWeight: 600 }}>
                        <i className={`mdi mdi-${expandido ? "chevron-down" : "chevron-right"} me-1`} />
                        {formatFecha(d.fecha)}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>{formatNumber(d.total_sucursales)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{formatNumber(tot.total_lineas)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{formatCurrency(tot.total_base_gravable)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 600, color: "#10b981" }}>
                        {formatCurrency(tot.total_impuestos)}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>{formatCurrency(tot.total_con_impuestos)}</td>
                      <td style={{ ...td, textAlign: "right", color: "#f59e0b" }}>{formatCurrency(totalExcluidoDia)}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        {pctExcluidoDia > 0 ? (
                          <span style={{
                            padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                            background: pctExcluidoDia >= 5 ? "#fef3c7" : "#f1f5f9",
                            color:      pctExcluidoDia >= 5 ? "#92400e" : "#475569",
                          }}>{formatPercent(pctExcluidoDia, 2)}</span>
                        ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <i className="mdi mdi-information-outline text-primary" />
                      </td>
                    </tr>
                    {expandido && (
                      <tr style={{ background: "#f8fafc" }}>
                        <td colSpan={9} style={{ padding: "12px 18px", borderBottom: "1px solid #f1f5f9" }}>
                          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
                            <i className="mdi mdi-source-branch me-1" />
                            Sucursales del día ({(d.sucursales || []).length})
                          </div>
                          {(d.sucursales || []).length === 0 ? (
                            <div style={{ color: "#94a3b8" }}>Sin movimientos.</div>
                          ) : (
                            <div style={{ overflowX: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    <th style={subTh}>Sucursal</th>
                                    <th style={subTh}>Familias</th>
                                    <th style={{ ...subTh, textAlign: "right" }}>Líneas</th>
                                    <th style={{ ...subTh, textAlign: "right" }}>Base</th>
                                    <th style={{ ...subTh, textAlign: "right" }}>Impuestos</th>
                                    <th style={{ ...subTh, textAlign: "right" }}>Excluidos</th>
                                    <th style={{ ...subTh, textAlign: "center" }}>% Excluido</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(d.sucursales || []).map((s, j) => {
                                    const t = s.totales_sucursal || {}
                                    const fams = Array.from(new Set((s.impuestos || []).map(x => x.familia)))
                                    const totalExcl = t.total_excluidos ?? t.total_exentos ?? s.excluidos?.importe ?? s.exentos?.base_gravable ?? 0
                                    const pctExcl = t.porcentaje_excluido ?? t.porcentaje_exento ?? 0
                                    return (
                                      <tr key={`${s.sucursal}-${j}`}>
                                        <td style={subTd}><strong>{s.sucursal}</strong></td>
                                        <td style={subTd}>
                                          <div style={{ display: "inline-flex", gap: 3, flexWrap: "wrap" }}>
                                            {fams.map(f => {
                                              const cfg = familiaConfig(f)
                                              return (
                                                <span key={f} style={{
                                                  padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700,
                                                  background: cfg.bg, color: cfg.color,
                                                }}>{f}</span>
                                              )
                                            })}
                                          </div>
                                        </td>
                                        <td style={{ ...subTd, textAlign: "right" }}>{formatNumber(t.total_lineas)}</td>
                                        <td style={{ ...subTd, textAlign: "right" }}>{formatCurrency(t.total_base_gravable)}</td>
                                        <td style={{ ...subTd, textAlign: "right", color: "#10b981", fontWeight: 600 }}>
                                          {formatCurrency(t.total_impuestos)}
                                        </td>
                                        <td style={{ ...subTd, textAlign: "right", color: "#f59e0b" }}>
                                          {formatCurrency(totalExcl)}
                                        </td>
                                        <td style={{ ...subTd, textAlign: "center" }}>
                                          {pctExcl > 0
                                            ? <strong>{formatPercent(pctExcl, 2)}</strong>
                                            : <span style={{ color: "#cbd5e1" }}>—</span>}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const miniCard = {
  flex: "1 1 200px",
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
  padding: "12px 14px",
}
const miniLabel = { fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }
const miniValue = { fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 4 }
const th = {
  background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
  padding: "10px 12px", textAlign: "left",
  fontSize: 11, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4, position: "sticky", top: 0, zIndex: 1,
}
const td = {
  padding: "10px 12px", borderBottom: "1px solid #f1f5f9",
  fontSize: 13, color: "#0f172a", verticalAlign: "middle",
}
const subTh = {
  padding: "6px 10px", textAlign: "left",
  fontSize: 10, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.3,
  borderBottom: "1px solid #e2e8f0",
}
const subTd = {
  padding: "6px 10px", borderBottom: "1px solid #e2e8f0",
  fontSize: 12, color: "#0f172a", verticalAlign: "middle",
}

DiarioTab.propTypes = { filtros: PropTypes.object }

export default DiarioTab
